// FIT import: reads a Garmin, Wahoo, Coros, Polar or Suunto activity file (the binary FIT format) in the
// browser. Only what the summary needs: the record messages (time, position, elevation, heart rate, cadence,
// distance), the session message (the device's own totals and sport) and the sport message.
// Our own small reader (decision in docs/architecture.md, "Cardio"): fit-file-parser (MIT) needs Node's Buffer
// and a package install, Garmin's @garmin/fitsdk is under the FIT protocol license (not an open-source
// license). The format is public (FIT protocol, Garmin); this reader follows it for the parts we use.
import type { ParsedTrack, TrackSample } from './activity';
import { MAX_TRACK_SAMPLES, TrackFileError } from './gpx';

export const MAX_FIT_BYTES = 25_000_000;

/** FIT time starts on 1989-12-31 00:00 UTC. */
const FIT_EPOCH_S = 631_065_600;

/** The FIT CRC-16 (from the FIT protocol), over a byte range. */
export function fitCrc(bytes: Uint8Array, start = 0, end = bytes.length, crc = 0): number {
  const table = [0x0000, 0xcc01, 0xd801, 0x1400, 0xf001, 0x3c00, 0x2800, 0xe401, 0xa001, 0x6c00, 0x7800, 0xb401, 0x5000, 0x9c01, 0x8801, 0x4400];
  for (let i = start; i < end; i++) {
    const b = bytes[i]!;
    let tmp = table[crc & 0xf]!;
    crc = (crc >> 4) & 0x0fff;
    crc = crc ^ tmp ^ table[b & 0xf]!;
    tmp = table[crc & 0xf]!;
    crc = (crc >> 4) & 0x0fff;
    crc = crc ^ tmp ^ table[(b >> 4) & 0xf]!;
  }
  return crc;
}

interface FieldDef { num: number; size: number; type: number }
interface Definition { global: number; little: boolean; fields: FieldDef[]; devSize: number }

/** Size in bytes and invalid value of each base type (low 5 bits of the base type byte). */
const BASE: Record<number, { size: number; invalid: number | null; signed?: boolean; float?: boolean }> = {
  0: { size: 1, invalid: 0xff }, 1: { size: 1, invalid: 0x7f, signed: true }, 2: { size: 1, invalid: 0xff },
  3: { size: 2, invalid: 0x7fff, signed: true }, 4: { size: 2, invalid: 0xffff },
  5: { size: 4, invalid: 0x7fffffff, signed: true }, 6: { size: 4, invalid: 0xffffffff },
  8: { size: 4, invalid: null, float: true }, 9: { size: 8, invalid: null, float: true },
  10: { size: 1, invalid: 0 }, 11: { size: 2, invalid: 0 }, 12: { size: 4, invalid: 0 },
};

function readValue(view: DataView, at: number, f: FieldDef, little: boolean): number | undefined {
  const base = BASE[f.type & 0x1f];
  if (!base || base.size !== f.size) return undefined; // arrays and strings: not needed here
  let v: number;
  if (base.float) v = base.size === 4 ? view.getFloat32(at, little) : view.getFloat64(at, little);
  else if (base.size === 1) v = base.signed ? view.getInt8(at) : view.getUint8(at);
  else if (base.size === 2) v = base.signed ? view.getInt16(at, little) : view.getUint16(at, little);
  else v = base.signed ? view.getInt32(at, little) : view.getUint32(at, little);
  if (!Number.isFinite(v) || (base.invalid !== null && v === base.invalid)) return undefined;
  return v;
}

/** FIT sport enum to words sportFromFile() understands. */
const SPORTS: Record<number, string> = {
  1: 'running', 2: 'cycling', 5: 'swimming', 11: 'walking', 12: 'cross_country_skiing', 15: 'rowing', 16: 'mountaineering',
  17: 'hiking', 30: 'inline_skating',
};
const SUB_SPORTS: Record<number, string> = {
  1: 'treadmill', 3: 'trail', 4: 'track', 5: 'spin', 6: 'indoor_cycling', 8: 'mountain', 9: 'downhill', 14: 'indoor_rowing',
  15: 'elliptical', 16: 'stair_climbing', 17: 'lap_swimming', 18: 'open_water', 37: 'backcountry', 46: 'gravel_cycling', 58: 'virtual_activity',
};

const MSG = { record: 20, session: 18, sport: 12 } as const;

export function parseFit(input: ArrayBuffer | Uint8Array): ParsedTrack {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
  if (bytes.length > MAX_FIT_BYTES) throw new TrackFileError('This FIT file is larger than 25 MB.');
  if (bytes.length < 14) throw new TrackFileError('This is not a FIT file (too short).');
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const headerSize = bytes[0]!;
  if ((headerSize !== 12 && headerSize !== 14) || String.fromCharCode(bytes[8]!, bytes[9]!, bytes[10]!, bytes[11]!) !== '.FIT') {
    throw new TrackFileError('This is not a FIT file (no FIT header).');
  }
  const dataSize = view.getUint32(4, true);
  const end = headerSize + dataSize;
  if (end + 2 > bytes.length) throw new TrackFileError('This FIT file is cut off.');
  if (fitCrc(bytes, 0, end) !== view.getUint16(end, true)) throw new TrackFileError('This FIT file is damaged (checksum does not match).');

  const defs = new Map<number, Definition>();
  const samples: TrackSample[] = [];
  let lastTimestamp: number | undefined;
  let sport: string | undefined;
  let subSport: string | undefined;
  let totals: NonNullable<ParsedTrack['totals']> | undefined;
  let startedAt: number | undefined;

  let at = headerSize;
  while (at < end) {
    const header = bytes[at++]!;
    let local: number;
    let compressedTime: number | undefined;
    if (header & 0x80) {
      // Compressed timestamp header: a data message with a 5-bit time offset from the last timestamp.
      local = (header >> 5) & 0x3;
      if (lastTimestamp !== undefined) {
        const offset = header & 0x1f;
        let t = (lastTimestamp & ~0x1f) + offset;
        if (offset < (lastTimestamp & 0x1f)) t += 0x20;
        compressedTime = t;
        lastTimestamp = t;
      }
    } else if (header & 0x40) {
      // Definition message.
      local = header & 0x0f;
      const hasDev = (header & 0x20) !== 0;
      if (at + 5 > end) throw new TrackFileError('This FIT file is damaged.');
      const little = bytes[at + 1] === 0;
      const global = view.getUint16(at + 2, little);
      const count = bytes[at + 4]!;
      at += 5;
      const fields: FieldDef[] = [];
      for (let i = 0; i < count; i++, at += 3) fields.push({ num: bytes[at]!, size: bytes[at + 1]!, type: bytes[at + 2]! });
      let devSize = 0;
      if (hasDev) {
        const devCount = bytes[at++]!;
        for (let i = 0; i < devCount; i++, at += 3) devSize += bytes[at + 1]!;
      }
      if (at > end) throw new TrackFileError('This FIT file is damaged.');
      defs.set(local, { global, little, fields, devSize });
      continue;
    } else {
      local = header & 0x0f;
    }
    const def = defs.get(local);
    if (!def) throw new TrackFileError('This FIT file is damaged (data before its definition).');
    const values = new Map<number, number>();
    for (const f of def.fields) {
      if (at + f.size > end) throw new TrackFileError('This FIT file is damaged.');
      const v = readValue(view, at, f, def.little);
      if (v !== undefined) values.set(f.num, v);
      at += f.size;
    }
    at += def.devSize;
    const ts = values.get(253);
    if (ts !== undefined) lastTimestamp = ts;
    const time = ts ?? compressedTime;

    if (def.global === MSG.record) {
      if (samples.length >= MAX_TRACK_SAMPLES) throw new TrackFileError(`This FIT file has more than ${MAX_TRACK_SAMPLES} points.`);
      const s: TrackSample = {};
      if (time !== undefined) s.time = (time + FIT_EPOCH_S) * 1000;
      const lat = values.get(0);
      const lon = values.get(1);
      if (lat !== undefined && lon !== undefined) {
        s.lat = lat * (180 / 2 ** 31);
        s.lon = lon * (180 / 2 ** 31);
      }
      const alt = values.get(78) ?? values.get(2);
      if (alt !== undefined) s.ele = alt / 5 - 500;
      const hr = values.get(3);
      if (hr !== undefined && hr > 0) s.hr = hr;
      const cad = values.get(4);
      if (cad !== undefined) s.cadence = cad;
      const dist = values.get(5);
      if (dist !== undefined) s.distanceM = dist / 100;
      samples.push(s);
    } else if (def.global === MSG.session) {
      const g = (n: number) => values.get(n);
      if (g(5) !== undefined) sport = SPORTS[g(5)!] ?? sport;
      if (g(6) !== undefined) subSport = SUB_SPORTS[g(6)!] ?? subSport;
      if (g(2) !== undefined) startedAt = (g(2)! + FIT_EPOCH_S) * 1000;
      const div = (n: number | undefined, by: number) => (n === undefined ? undefined : n / by);
      const t = {
        durationSec: div(g(8), 1000), elapsedSec: div(g(7), 1000), distanceM: div(g(9), 100),
        elevationGainM: g(22), elevationLossM: g(23), avgHr: g(16), maxHr: g(17), avgCadence: g(18), poolLengthM: div(g(44), 100),
      };
      totals = Object.fromEntries(Object.entries(t).filter(([, v]) => v !== undefined)) as NonNullable<ParsedTrack['totals']>;
    } else if (def.global === MSG.sport) {
      if (!sport && values.get(0) !== undefined) sport = SPORTS[values.get(0)!];
      if (!subSport && values.get(1) !== undefined) subSport = SUB_SPORTS[values.get(1)!];
    }
  }
  if (samples.length === 0 && !totals) throw new TrackFileError('This FIT file has no activity records.');
  // Elliptical and stair machines are "fitness equipment" with a sub-sport.
  if (!sport && (subSport === 'elliptical' || subSport === 'stair_climbing' || subSport === 'indoor_rowing')) sport = subSport === 'indoor_rowing' ? 'rowing' : subSport;
  return {
    format: 'fit',
    ...(sport ? { sportHint: sport } : {}),
    ...(subSport ? { subSportHint: subSport } : {}),
    samples,
    ...(totals && Object.keys(totals).length ? { totals } : {}),
    ...(startedAt !== undefined ? { startedAt } : {}),
  };
}
